import React, { useRef, useState } from 'react';
import { SearchFilters, ParsedFilters } from '../../types';
import './RaceFilters.css';

interface Props {
  filters: SearchFilters;
  onChange: (f: Partial<SearchFilters>) => void;
  onSearch: () => void;
  loading: boolean;
  parsedFilters?: ParsedFilters;
  resolvedLocation?: string;
}

const DISTANCES = [
  { key: '5k',       label: '5K' },
  { key: '10k',      label: '10K' },
  { key: '10m',      label: '10 Mile' },
  { key: 'half',     label: 'Half' },
  { key: 'marathon', label: 'Marathon' },
  { key: '50k',      label: '50K' },
  { key: '50m',      label: '50 Mile' },
  { key: '100k',     label: '100K' },
  { key: '100m',     label: '100 Mile' },
];

const TERRAINS = [
  { key: 'road',  label: 'Road' },
  { key: 'trail', label: 'Trail' },
  { key: 'track', label: 'Track' },
  { key: 'mixed', label: 'Mixed' },
];

const RADIUS_OPTIONS = [10, 25, 50, 100];

function fmtDate(iso: string) {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const RaceFilters: React.FC<Props> = ({ filters, onChange, onSearch, loading, parsedFilters, resolvedLocation }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const toggleDistance = (key: string) => {
    const current = filters.distances;
    onChange({ distances: current.includes(key) ? current.filter(d => d !== key) : [...current, key] });
  };

  const toggleTerrain = (key: string) => {
    const current = filters.terrains;
    onChange({ terrains: current.includes(key) ? current.filter(t => t !== key) : [...current, key] });
  };

  const setDatePreset = (days: number) => {
    const from = new Date();
    const to = new Date();
    to.setDate(to.getDate() + days);
    onChange({ date_from: from.toISOString().slice(0, 10), date_to: to.toISOString().slice(0, 10) });
  };

  const clearAll = () => {
    onChange({
      nl_query: '', q: '', state: '', city: '', zip: '', radius_miles: 50,
      date_from: '', date_to: '', distances: [], terrains: [], status: '', sort: 'date',
      lat: undefined, lng: undefined,
    });
  };

  // Count active filters for the badge (excluding NL query — that's the search bar)
  const activeFilterCount = [
    filters.zip || filters.city || filters.state || (filters.lat != null),
    filters.date_from || filters.date_to,
    ...filters.distances,
    ...filters.terrains,
    filters.status && filters.status !== '',
  ].filter(Boolean).length;

  // Active filter chips shown inline below the search bar
  const activeChips: { label: string; onRemove: () => void }[] = [];

  const locationLabel = resolvedLocation || filters.zip || filters.city || filters.state;
  if (locationLabel) {
    activeChips.push({
      label: `📍 ${locationLabel}`,
      onRemove: () => onChange({ zip: '', city: '', state: '', lat: undefined, lng: undefined }),
    });
  } else if (filters.lat != null) {
    activeChips.push({
      label: '📍 Near you',
      onRemove: () => onChange({ lat: undefined, lng: undefined }),
    });
  }

  if (filters.date_from || filters.date_to) {
    const label = filters.date_from && filters.date_to
      ? `${fmtDate(filters.date_from)} – ${fmtDate(filters.date_to)}`
      : filters.date_from ? `From ${fmtDate(filters.date_from)}` : `Until ${fmtDate(filters.date_to!)}`;
    activeChips.push({ label: `📅 ${label}`, onRemove: () => onChange({ date_from: '', date_to: '' }) });
  }

  filters.distances.forEach(d => {
    const dist = DISTANCES.find(x => x.key === d);
    if (dist) activeChips.push({ label: dist.label, onRemove: () => toggleDistance(d) });
  });

  filters.terrains.forEach(t => {
    const terr = TERRAINS.find(x => x.key === t);
    if (terr) activeChips.push({ label: terr.label, onRemove: () => toggleTerrain(t) });
  });

  // NL interpretation pills (shown after an NL search)
  const interpretations: string[] = [];
  if (parsedFilters?.distances?.length) {
    const labels: Record<string, string> = { '5k': '5K', '10k': '10K', half: 'Half Marathon', marathon: 'Marathon', '50k': '50K', '50m': '50 Mile', '100k': '100K', '100m': '100 Mile' };
    interpretations.push(parsedFilters.distances.map(d => labels[d] || d).join(', '));
  }
  if (parsedFilters?.terrain) interpretations.push(parsedFilters.terrain);
  if (parsedFilters?.date_from && parsedFilters?.date_to) {
    const from = new Date(parsedFilters.date_from).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const to   = new Date(parsedFilters.date_to).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    interpretations.push(`${from} – ${to}`);
  }

  return (
    <div className="race-filterbar">

      {/* ── Hero search bar ─────────────────────────────────────────── */}
      <div className="race-filterbar-hero">
        <div className="race-filterbar-nl-input-wrap">
          <svg className="race-filterbar-nl-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            ref={inputRef}
            className="race-filterbar-nl-input"
            type="text"
            placeholder='Try "half marathons near San Francisco in June" or "trail 50K Colorado"'
            value={filters.nl_query}
            onChange={e => onChange({ nl_query: e.target.value })}
            onKeyDown={e => e.key === 'Enter' && onSearch()}
          />
          {filters.nl_query && (
            <button className="race-filterbar-nl-clear" onClick={() => { onChange({ nl_query: '' }); inputRef.current?.focus(); }}>
              ×
            </button>
          )}
        </div>
        <button className="race-filterbar-search-btn" onClick={onSearch} disabled={loading}>
          {loading ? <span className="race-filterbar-spinner" /> : 'Search'}
        </button>
      </div>

      {/* ── Filter toggle + active chips ────────────────────────────── */}
      <div className="race-filterbar-meta">
        <button
          className={`race-filterbar-toggle ${filtersOpen ? 'open' : ''}`}
          onClick={() => setFiltersOpen(v => !v)}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/>
          </svg>
          Filters
          {activeFilterCount > 0 && (
            <span className="race-filterbar-badge">{activeFilterCount}</span>
          )}
          <svg className={`race-filterbar-chevron ${filtersOpen ? 'open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>

        {activeChips.map((chip, i) => (
          <span key={i} className="race-active-chip">
            {chip.label}
            <button className="race-active-chip-remove" onClick={chip.onRemove}>×</button>
          </span>
        ))}

        {activeFilterCount > 0 && (
          <button className="race-filterbar-clear-all" onClick={clearAll}>Clear all</button>
        )}
      </div>

      {/* ── NL interpretation strip ─────────────────────────────────── */}
      {interpretations.length > 0 && (
        <div className="race-filterbar-interpretation">
          <span className="race-filterbar-interpretation-label">Searching for:</span>
          {interpretations.map((item, i) => (
            <span key={i} className="race-filterbar-interpretation-pill">{item}</span>
          ))}
        </div>
      )}

      {/* ── Collapsible filter panel ────────────────────────────────── */}
      {filtersOpen && (
        <div className="race-filterbar-panel">

          {/* Row 1: Location + Date */}
          <div className="race-filterbar-panel-row">
            <div className="race-filterbar-panel-group">
              <span className="race-filterbar-panel-label">Near</span>
              <div className="race-filterbar-panel-group-inline">
                <input
                  className="race-filterbar-input race-filterbar-input-zip"
                  type="text"
                  placeholder="ZIP or city"
                  value={filters.zip || filters.city}
                  onChange={e => {
                    const v = e.target.value;
                    /^\d/.test(v) ? onChange({ zip: v, city: '' }) : onChange({ city: v, zip: '' });
                  }}
                  onKeyDown={e => e.key === 'Enter' && onSearch()}
                />
                <select
                  className="race-filterbar-select"
                  value={filters.radius_miles || 50}
                  onChange={e => onChange({ radius_miles: Number(e.target.value) })}
                >
                  {RADIUS_OPTIONS.map(r => <option key={r} value={r}>{r} mi</option>)}
                </select>
              </div>
            </div>

            <div className="race-filterbar-panel-group">
              <span className="race-filterbar-panel-label">When</span>
              <div className="race-filterbar-panel-group-inline">
                {[{ label: '30d', days: 30 }, { label: '3mo', days: 90 }, { label: '6mo', days: 180 }].map(({ label, days }) => (
                  <button key={label} className="race-filterbar-preset" onClick={() => setDatePreset(days)}>{label}</button>
                ))}
                <input className="race-filterbar-input race-filterbar-date" type="date" value={filters.date_from} onChange={e => onChange({ date_from: e.target.value })} />
                <span className="race-filterbar-date-arrow">→</span>
                <input className="race-filterbar-input race-filterbar-date" type="date" value={filters.date_to} onChange={e => onChange({ date_to: e.target.value })} />
              </div>
            </div>
          </div>

          {/* Row 2: Distance + Terrain */}
          <div className="race-filterbar-panel-row">
            <div className="race-filterbar-panel-group">
              <span className="race-filterbar-panel-label">Distance</span>
              <div className="race-filterbar-panel-group-inline">
                {DISTANCES.map(d => (
                  <button
                    key={d.key}
                    className={`race-filterbar-chip ${filters.distances.includes(d.key) ? 'active' : ''}`}
                    onClick={() => toggleDistance(d.key)}
                  >{d.label}</button>
                ))}
              </div>
            </div>

            <div className="race-filterbar-panel-group">
              <span className="race-filterbar-panel-label">Terrain</span>
              <div className="race-filterbar-panel-group-inline">
                {TERRAINS.map(t => (
                  <button
                    key={t.key}
                    className={`race-filterbar-chip ${filters.terrains.includes(t.key) ? 'active' : ''}`}
                    onClick={() => toggleTerrain(t.key)}
                  >{t.label}</button>
                ))}
              </div>
            </div>
          </div>

          {/* Row 3: Sort + Status */}
          <div className="race-filterbar-panel-row race-filterbar-panel-row--footer">
            <div className="race-filterbar-panel-group-inline">
              <span className="race-filterbar-panel-label">Sort</span>
              <select className="race-filterbar-select" value={filters.sort} onChange={e => onChange({ sort: e.target.value })}>
                <option value="date">Date ↑</option>
                <option value="rating">Rating</option>
                <option value="name">A–Z</option>
              </select>
              <span className="race-filterbar-panel-label" style={{ marginLeft: '0.75rem' }}>Status</span>
              <select className="race-filterbar-select" value={filters.status} onChange={e => onChange({ status: e.target.value })}>
                <option value="">Any</option>
                <option value="open">Open</option>
                <option value="upcoming">Coming Soon</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RaceFilters;
